import type { FormatZodErrorOptions } from "@Simplicity-Education/core";
import { apiErrorFrom } from "../../api";
import type {
  AgentReleaseRecord,
  AgentReleases,
  CreateWorkspaceInput,
  OnsiteRun,
  OnsiteWorkspace,
  PromoteReleaseInput,
  SftpUsersChange,
  WorkspaceSettingsPatch,
} from "./types";

const apiBase = import.meta.env.VITE_API_URL ?? "";
const ONSITE_BASE = "/api/admin/onsite";
const RUNS_LIMIT = 20;

const usernameIssue: NonNullable<FormatZodErrorOptions["overrides"]>[string] = (issue) =>
  `Username: ${issue.message ?? "invalid"}`;

const ISSUE_OVERRIDES: NonNullable<FormatZodErrorOptions["overrides"]> = {
  name: (issue) => `Name: ${issue.message ?? "invalid"}`,
  gcpProjectId: (issue) => `GCP project id: ${issue.message ?? "invalid"}`,
  "sshSourceRanges.*": (issue) =>
    `SSH source range ${Number(issue.path[1]) + 1}: ${issue.message ?? "invalid"}`,
  region: (issue) => `Region: ${issue.message ?? "invalid"}`,
  zone: (issue) => `Zone: ${issue.message ?? "invalid"}`,
  "add.*": usernameIssue,
  "rotate.*": usernameIssue,
  "remove.*": usernameIssue,
  note: (issue) => `Note: ${issue.message ?? "invalid"}`,
};

/**
 * Like `apiFetch`, except a 403 is an answer rather than a lost session: approve-apply refuses
 * anyone outside its approver list, and the operator needs to read that reason.
 */
async function onsiteFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBase}${ONSITE_BASE}${path}`, {
    credentials: "include",
    ...init,
    headers: { "Content-Type": "application/json", ...init?.headers },
  });
  if (response.status === 401) {
    const base = window.location.pathname.startsWith("/web-admin") ? "/web-admin" : "";
    window.location.href = `${base}/login`;
    throw new Error("Unauthorized");
  }
  if (!response.ok) {
    throw await apiErrorFrom(response, { overrides: ISSUE_OVERRIDES });
  }
  return (await response.json()) as T;
}

export function listWorkspaces(): Promise<{ workspaces: OnsiteWorkspace[] }> {
  return onsiteFetch("/workspaces");
}

export function createWorkspace(input: CreateWorkspaceInput): Promise<OnsiteWorkspace> {
  return onsiteFetch("/workspaces", { method: "POST", body: JSON.stringify(input) });
}

export function updateWorkspace(
  workspaceId: string,
  patch: WorkspaceSettingsPatch,
): Promise<OnsiteWorkspace> {
  return onsiteFetch(`/workspaces/${encodeURIComponent(workspaceId)}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export function requestPlan(workspaceId: string): Promise<OnsiteRun> {
  return onsiteFetch(`/workspaces/${encodeURIComponent(workspaceId)}/plan`, {
    method: "POST",
    body: "{}",
  });
}

export function listRuns(workspaceId: string): Promise<{ runs: OnsiteRun[] }> {
  const query = new URLSearchParams({ workspaceId, limit: String(RUNS_LIMIT) });
  return onsiteFetch(`/runs?${query.toString()}`);
}

export function approveApply(planRunId: number): Promise<OnsiteRun> {
  return onsiteFetch(`/runs/${planRunId}/approve-apply`, { method: "POST", body: "{}" });
}

/** Queues an sftp-users run. Approver-only, like approve-apply, so a 403 carries a reason. */
export function changeSftpUsers(workspaceId: string, body: SftpUsersChange): Promise<OnsiteRun> {
  return onsiteFetch(`/workspaces/${encodeURIComponent(workspaceId)}/sftp-users`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function listAgentReleases(): Promise<AgentReleases> {
  return onsiteFetch("/releases");
}

/** Approver-only; the server looks the digest up in its own build list and says why it refuses. */
export function promoteAgentRelease(body: PromoteReleaseInput): Promise<AgentReleaseRecord> {
  return onsiteFetch("/releases/promote", { method: "POST", body: JSON.stringify(body) });
}

/** Approver-only: re-promotes the release before the current one. */
export function rollbackAgentRelease(note: string): Promise<AgentReleaseRecord> {
  return onsiteFetch("/releases/rollback", { method: "POST", body: JSON.stringify({ note }) });
}

/** Queues an sftp-users run that only reads the secret and reports its users. Approver-only too. */
export function refreshSftpUsers(workspaceId: string): Promise<OnsiteRun> {
  return onsiteFetch(`/workspaces/${encodeURIComponent(workspaceId)}/sftp-users/refresh`, {
    method: "POST",
    body: "{}",
  });
}
